import { useState, useEffect } from 'react';
import classNames from 'classnames';
import { LinkedLogo } from '../../../../components';

import Field from '../../Field';
import BlockBuilder from '../../BlockBuilder';

import SectionContainer from '../SectionContainer';
import css from './SectionFooter.module.css';

import EUFooterHr from '../../../../../src/assets/EU_footer.svg'
import EUFooterEn from '../../../../../src/assets/EU_footer_en.svg'
import { P } from '../../Primitives/P';

// The number of columns (numberOfColumns) affects styling

const GRID_CONFIG = [
  { contentCss: css.contentCol1, gridCss: css.gridCol1 },
  { contentCss: css.contentCol2, gridCss: css.gridCol2 },
  { contentCss: css.contentCol3, gridCss: css.gridCol3 },
  { contentCss: css.contentCol4, gridCss: css.gridCol4 },
];
const MAX_MOBILE_SCREEN_WIDTH = 1024;

const getIndex = numberOfColumns => numberOfColumns - 1;

const getContentCss = numberOfColumns => {
  const contentConfig = GRID_CONFIG[getIndex(numberOfColumns)];
  return contentConfig ? contentConfig.contentCss : GRID_CONFIG[0].contentCss;
};

const getGridCss = numberOfColumns => {
  const contentConfig = GRID_CONFIG[getIndex(numberOfColumns)];
  return contentConfig ? contentConfig.gridCss : GRID_CONFIG[0].gridCss;
};

/**
 * @typedef {Object} SocialMediaLinkConfig
 * @property {'socialMediaLink'} fieldType
 * @property {string} platform
 * @property {string} url
 */

/**
 * @typedef {Object} BlockConfig
 * @property {string} blockId
 * @property {string} blockName
 * @property {'defaultBlock' | 'footerBlock' | 'socialMediaLink'} blockType
 */

/**
 * @typedef {Object} FieldComponentConfig
 * @property {ReactNode} component
 * @property {Function} pickValidProps
 */

/**
 * Section component that's able to show blocks in multiple different columns (defined by "numberOfColumns" prop)
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string} props.sectionId id of the section
 * @param {'footer'} props.sectionType
 * @param {number} props.numberOfColumns columns for blocks in footer (1-4)
 * @param {Array<SocialMediaLinkConfig>?} props.socialMediaLinks array of social media link configs
 * @param {Object?} props.slogan
 * @param {Object?} props.copyright
 * @param {Object?} props.appearance
 * @param {Array<BlockConfig>?} props.blocks array of block configs
 * @param {Object} props.options extra options for the section component (e.g. custom fieldComponents)
 * @param {Object<string,FieldComponentConfig>?} props.options.fieldComponents custom fields
 * @returns {JSX.Element} Section for article content
 */
const SectionFooter = props => {
  const {
    sectionId,
    className,
    rootClassName,
    numberOfColumns = 1,
    socialMediaLinks = [],
    slogan,
    appearance,
    copyright,
    blocks = [],
    options,
    linkLogoToExternalSite,
  } = props;

  // If external mapping has been included for fields
  // E.g. { h1: { component: MyAwesomeHeader } }
  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };
  const linksWithBlockId = socialMediaLinks?.map(sml => {
    return {
      ...sml,
      blockId: sml.link.platform,
    };
  });

  const showSocialMediaLinks = socialMediaLinks?.length > 0;
  const hasMatchMedia = typeof window !== 'undefined' && window?.matchMedia;
  const isMobileLayout = hasMatchMedia
    ? window.matchMedia(`(max-width: ${MAX_MOBILE_SCREEN_WIDTH}px)`)?.matches
    : true;
  const logoLayout = isMobileLayout ? 'mobile' : 'desktop';

  const locale = (typeof window !== "undefined" && localStorage.getItem("locale")) || "en";

  if (typeof window !== "undefined") {
    if (locale == 'hr') {
      blocks[0].text.content =
        '- [O nama](/p/about-hr) \n- [GDPR i Politika kolačića](/p/gdpr-cookie-policy-hr) \n- [Izjava o privatnosti](/p/privacy-policy-hr)';
    } else {
      blocks[0].text.content =
        '- [About us](/p/about) \n- [GDPR & Cookie Policy](/p/gdpr-cookie-policy) \n- [Privacy Policy](/privacy-policy)';
    }
  }

  return (
    <SectionContainer
      as="footer"
      id={sectionId}
      className={className || css.root}
      rootClassName={rootClassName}
      appearance={appearance}
      options={fieldOptions}
    >
      <div className={css.footer}>
        <div className={classNames(css.content, css.customContent, getContentCss(numberOfColumns))}>
          <div>
            <LinkedLogo
              rootClassName={css.logoLink}
              logoClassName={css.logoWrapper}
              logoImageClassName={css.logoImage}
              linkToExternalSite={linkLogoToExternalSite}
              layout={logoLayout}
            />
          </div>
          {locale === 'en' && (
            <div className={css.sloganMobile}>
              <Field data={slogan} className={css.slogan} />
            </div>
          )}
          <div className={css.detailsInfo}>
            {locale === 'en' && (
              <div className={css.sloganDesktop}>
                <Field data={slogan} className={css.slogan} />
              </div>
            )}
            {locale === 'hr' && (
              <div className={css.sloganDesktop}>
                <p>Živi sad!</p>
              </div>
            )}
            {showSocialMediaLinks ? (
              <div className={css.icons}>
                <BlockBuilder blocks={linksWithBlockId} sectionId={sectionId} options={options} />
              </div>
            ) : null}
            {/* Copyright, presumably localized */}
            {locale === 'en' && (
              <Field data={copyright} className={css.copyright} />
            )}
            {locale === 'hr' && (
              <p style={{
                color: "silver",
                fontSize: "12px"
              }}>© 2024 16 milja. Sva prava pridržana.</p>
            )}
          </div>
          <div className={classNames(getGridCss(numberOfColumns), css.grid, css.customGrid)}>
            <>
              <BlockBuilder blocks={blocks} sectionId={sectionId} options={options} />
              <div style={{ textAlign: "right" }}>
                <img
                  src={EUFooterEn}
                  alt="EU Footer"
                />
              </div>
            </>
          </div>
        </div>
      </div>
    </SectionContainer>
  );
};

export default SectionFooter;
